/* eslint-disable react-refresh/only-export-components */
import { isValidElement } from 'react';

import { Link } from 'react-router';

import { Card, ProgressBar } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

/* ------------------------------------------------------------ */

/** Stable across Vite HMR; Root matches slots by this, not `child.type`. */
const PATHWAY_CARD_SLOT = {
  media: 'PathwayCard.Media',
  body: 'PathwayCard.Body',
  meta: 'PathwayCard.Meta',
  actions: 'PathwayCard.Actions',
  progress: 'PathwayCard.Progress',
} as const;

type PathwayCardSlot =
  (typeof PATHWAY_CARD_SLOT)[keyof typeof PATHWAY_CARD_SLOT];

type PathwayCardSlotComponent<P = object> = React.FC<P> & {
  pathwayCardSlot: PathwayCardSlot;
};

const getPathwayCardSlot = (type: unknown): PathwayCardSlot | undefined => {
  if (typeof type !== 'function') {
    return undefined;
  }
  return (type as Partial<PathwayCardSlotComponent>).pathwayCardSlot;
};

const definePathwayCardSlot = <P,>(
  slot: PathwayCardSlot,
  component: React.FC<P>,
): PathwayCardSlotComponent<P> => {
  const marked = component as PathwayCardSlotComponent<P>;
  marked.pathwayCardSlot = slot;
  return marked;
};

const findChildBySlot = (
  children: readonly (React.ReactElement | false | '' | undefined)[],
  slot: PathwayCardSlot,
) =>
  children.find(
    child => isValidElement(child) && getPathwayCardSlot(child.type) === slot,
  );

/* ------------------------------------------------------------ */

export interface MediaProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const Media = definePathwayCardSlot<MediaProps>(
  PATHWAY_CARD_SLOT.media,
  ({ children, className, ...props }) => (
    <div
      className={cn(
        'flex shrink-0 items-stretch justify-center self-stretch',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);

type MediaElement = React.ReactElement<MediaProps, typeof Media>;

/* ------------------------------------------------------------ */

export interface LabelProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children?: React.ReactNode;
}

const Label: React.FC<LabelProps> = ({ children, className, ...props }) => (
  <p
    data-testid="PlanCardLabel"
    // tailwind-merge reads `text-variant-*` as a text color and drops it next to `text-contrast`.
    className={`text-variant-overline3 ${cn(
      'text-contrast max-w-full min-w-0 overflow-hidden wrap-break-word text-ellipsis',
      className,
    )}`}
    {...props}
  >
    {children}
  </p>
);

type LabelElement = React.ReactElement<LabelProps, typeof Label>;

/* ------------------------------------------------------------ */

export interface TitleProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children?: React.ReactNode;
}

const Title: React.FC<TitleProps> = ({ children, className, ...props }) => (
  <p
    data-testid="PlanName"
    className={cn(
      'cfa-typography cfa-typography-variant-body1 cfa-typography-color__default weight-bold line-clamp-2 max-w-full min-w-0 overflow-hidden text-ellipsis',
      className,
    )}
    {...props}
  >
    {children}
  </p>
);

type TitleElement = React.ReactElement<TitleProps, typeof Title>;

/* ------------------------------------------------------------ */

export interface SubtitleProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children?: React.ReactNode;
  tone?: 'default' | 'success';
}

const Subtitle: React.FC<SubtitleProps> = ({
  children,
  className,
  tone = 'default',
  ...props
}) => (
  <p
    className={`text-variant-body2 ${cn(
      'text-contrast max-w-full min-w-0 overflow-hidden pb-1 wrap-break-word text-ellipsis',
      tone === 'success' && 'text-brand-green! font-bold!',
      className,
    )}`}
    {...props}
  >
    {children}
  </p>
);

type SubtitleElement = React.ReactElement<SubtitleProps, typeof Subtitle>;

/* ------------------------------------------------------------ */

type BodyChild =
  | LabelElement
  | TitleElement
  | SubtitleElement
  | undefined
  | false
  | '';

export interface BodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: BodyChild | BodyChild[];
}

const Body = definePathwayCardSlot<BodyProps>(
  PATHWAY_CARD_SLOT.body,
  ({ children, className, ...props }) => (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5 text-left',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);

type BodyElement = React.ReactElement<BodyProps, typeof Body>;

/* ------------------------------------------------------------ */

export interface MetaProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const Meta = definePathwayCardSlot<MetaProps>(
  PATHWAY_CARD_SLOT.meta,
  ({ children, className, ...props }) => (
    <div
      className={cn(
        'flex shrink-0 items-center justify-end pb-2 sm:pb-0',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);

type MetaElement = React.ReactElement<MetaProps, typeof Meta>;

/* ------------------------------------------------------------ */

export interface ActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const Actions = definePathwayCardSlot<ActionsProps>(
  PATHWAY_CARD_SLOT.actions,
  ({ children, className, ...props }) => (
    <div
      className={cn(
        'ml-4 flex shrink-0 items-center justify-center self-center',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);

type ActionsElement = React.ReactElement<ActionsProps, typeof Actions>;

/* ------------------------------------------------------------ */

export interface ProgressProps {
  value: number;
  className?: string;
  'aria-label'?: string;
}

const Progress = definePathwayCardSlot<ProgressProps>(
  PATHWAY_CARD_SLOT.progress,
  ({
    value,
    className,
    'aria-label': ariaLabel = 'Progress',
  }: ProgressProps) => (
    <div className={cn('w-full', className)}>
      <ProgressBar
        aria-label={ariaLabel}
        className="[&>span]:hidden"
        color="success"
        compact
        value={value}
      />
    </div>
  ),
);

type ProgressElement = React.ReactElement<ProgressProps, typeof Progress>;

/* ------------------------------------------------------------ */

type PathwayCardElement =
  | MediaElement
  | BodyElement
  | MetaElement
  | ActionsElement
  | ProgressElement
  | undefined
  | false
  | '';

export interface RootProps {
  children?: PathwayCardElement | PathwayCardElement[];
  /**
   * When set and `noHover` is false, wraps Body/Meta (and the Media rail, via an
   * `aria-hidden` duplicate) in a react-router `Link`. Progress and Actions stay
   * outside so the bar and menus do not navigate.
   */
  link?: string | null;
  /**
   * Disables hover elevation/border and link wrapping (disabled/archived cards).
   */
  noHover?: boolean;
  className?: string;
  'data-testid'?: string;
}

const Root: React.FC<RootProps> = ({
  children,
  link,
  noHover = false,
  className,
  'data-testid': dataTestId,
}) => {
  const childrenAsArray = Array.isArray(children)
    ? children
    : children
      ? [children]
      : [];

  const media = findChildBySlot(childrenAsArray, PATHWAY_CARD_SLOT.media);
  const body = findChildBySlot(childrenAsArray, PATHWAY_CARD_SLOT.body);
  const meta = findChildBySlot(childrenAsArray, PATHWAY_CARD_SLOT.meta);
  const actions = findChildBySlot(childrenAsArray, PATHWAY_CARD_SLOT.actions);
  const progress = findChildBySlot(childrenAsArray, PATHWAY_CARD_SLOT.progress);

  const linkTo = link && !noHover ? link : null;

  const bodyAndMeta = (
    <div className="flex min-w-0 flex-1 flex-col items-start sm:flex-row sm:items-center">
      {body}
      {meta}
    </div>
  );

  return (
    <Card.Root
      outlined
      data-testid={dataTestId}
      className={cn(
        'pathway-card shadow-elevation-1! relative h-auto w-full max-w-full overflow-hidden p-0!',
        !noHover && 'hover:shadow-elevation-2!',
        noHover && 'no-hover cursor-default',
        className,
      )}
    >
      {/* Media is a flush full-height rail; padding lives on the content column. */}
      <div className="flex items-stretch">
        {media &&
          (linkTo ? (
            // Duplicate of the main link so the rail is clickable; hidden from AT and tab order.
            <Link
              to={linkTo}
              tabIndex={-1}
              aria-hidden
              className="flex shrink-0"
            >
              {media}
            </Link>
          ) : (
            <div className="flex shrink-0">{media}</div>
          ))}
        <div className="flex min-w-0 flex-1 flex-col justify-center p-4">
          <div className="flex">
            {linkTo ? (
              <Link
                to={linkTo}
                className="flex min-w-0 flex-1 flex-col justify-center"
              >
                {bodyAndMeta}
              </Link>
            ) : (
              <div className="flex min-w-0 flex-1 flex-col justify-center">
                {bodyAndMeta}
              </div>
            )}
            {actions}
          </div>
          {progress}
        </div>
      </div>
    </Card.Root>
  );
};

/* ------------------------------------------------------------ */

interface PathwayCard {
  /**
   * Outlined react-core Card shell with baked-in `shadow-elevation-1`.
   * Hover lifts to `shadow-elevation-2` unless `noHover`.
   *
   * @example
   * <PathwayCard.Root link="/plans/1">
   *   <PathwayCard.Media />
   *   <PathwayCard.Body>
   *     <PathwayCard.Label>Category</PathwayCard.Label>
   *     <PathwayCard.Title>Plan name</PathwayCard.Title>
   *     <PathwayCard.Subtitle>Due soon</PathwayCard.Subtitle>
   *   </PathwayCard.Body>
   *   <PathwayCard.Progress value={40} />
   * </PathwayCard.Root>
   */
  Root: React.FC<RootProps>;
  /** Left rail / icon slot (flexible children — not react-core Card.Media). */
  Media: React.FC<MediaProps>;
  /** Text column wrapper for Label / Title / Subtitle. */
  Body: React.FC<BodyProps>;
  /** Overline label above the title. */
  Label: React.FC<LabelProps>;
  /** Primary bold title. */
  Title: React.FC<TitleProps>;
  /** Secondary body text under the title. */
  Subtitle: React.FC<SubtitleProps>;
  /** Optional secondary column (badges, dates, etc.). */
  Meta: React.FC<MetaProps>;
  /** Actions outside the link (menus, buttons). */
  Actions: React.FC<ActionsProps>;
  /** Compact success ProgressBar at the bottom of the main content. */
  Progress: React.FC<ProgressProps>;
}

/**
 * Composable Pathway list-row card on `@cfa/react-core` Card.
 *
 * Slot placement uses `pathwayCardSlot` on each part (HMR-safe); JSX order under
 * Root does not matter. Label / Title / Subtitle nest under Body.
 */
export const PathwayCard: PathwayCard = {
  Root,
  Media,
  Body,
  Label,
  Title,
  Subtitle,
  Meta,
  Actions,
  Progress,
};
